import {
  format as dfFormat,
  parse as dfParse,
  parseISO,
} from 'date-fns';

// moment's default `.format()` output: ISO-8601 to the second with a numeric
// offset (e.g. "+02:00", and "+00:00" rather than "Z" at zero offset).
const ISO_DEFAULT_PATTERN = "yyyy-MM-dd'T'HH:mm:ssxxx";

// The string moment.js returns from `.format()` on an invalid date. date-fns
// THROWS in that situation, so every formatter below degrades to this string
// instead — preserving the old (non-crashing) behaviour for null/empty/garbage.
const INVALID_DATE = 'Invalid date';

function isValidDate(date: Date): boolean {
  return !isNaN(date.getTime());
}

const TIMEZONE_SUFFIX_RE = /Z|[+-]\d{2}:?\d{2}$/;

/**
 * Parse a value as UTC when it has no timezone suffix, mirroring
 * `moment.utc(value).local()`. A bare datetime string ("2026-01-05T09:07:03")
 * is treated as UTC; a Date is returned as-is.
 */
export function parseUtcDate(value: string | Date | null | undefined): Date {
  if (!value) return new Date(NaN);
  if (value instanceof Date) return value;

  const hasTimezone = TIMEZONE_SUFFIX_RE.test(value);
  return new Date(hasTimezone ? value : value + 'Z');
}

// --- moment -> date-fns format-token translation -----------------------------

/** Mapping of moment.js format tokens to date-fns (Unicode LDML) tokens. */
const MOMENT_TO_DATE_FNS: Record<string, string> = {
  YYYY: 'yyyy', YY: 'yy',
  MMMM: 'MMMM', MMM: 'MMM', MM: 'MM', M: 'M',
  DD: 'dd', D: 'd',
  dddd: 'EEEE', ddd: 'EEE', dd: 'EEEEEE', d: 'e',
  HH: 'HH', H: 'H', hh: 'hh', h: 'h',
  mm: 'mm', m: 'm',
  ss: 'ss', s: 's',
  SSS: 'SSS', SS: 'SS', S: 'S',
  A: 'a', a: 'aaa',
  ZZ: 'xx', Z: 'xxx',
  X: 't', x: 'T',
};

// Longest tokens first so greedy matching consumes e.g. "YYYY" before "YY".
const MOMENT_TOKENS = Object.keys(MOMENT_TO_DATE_FNS).sort((a, b) => b.length - a.length);

/**
 * Convert a moment.js format string into the equivalent date-fns pattern.
 * Literal letters that are not tokens (e.g. the "T" in an ISO pattern) are
 * escaped, and moment's `[literal]` escapes become date-fns `'literal'`.
 */
export function momentToDateFnsFormat(momentFormat: string): string {
  let out = '';
  let i = 0;

  while (i < momentFormat.length) {
    const ch = momentFormat[i];

    // moment literal escape: [text]
    if (ch === '[') {
      const end = momentFormat.indexOf(']', i);
      const literal = end === -1 ? momentFormat.slice(i + 1) : momentFormat.slice(i + 1, end);
      out += `'${literal.replace(/'/g, "''")}'`;
      i = end === -1 ? momentFormat.length : end + 1;
      continue;
    }

    // known moment token (greedy, longest first)
    const token = MOMENT_TOKENS.find(t => momentFormat.startsWith(t, i));
    if (token) {
      out += MOMENT_TO_DATE_FNS[token];
      i += token.length;
      continue;
    }

    // unrecognised latin letter -> escape as a date-fns literal (e.g. "T")
    if (/[a-zA-Z]/.test(ch)) {
      out += `'${ch}'`;
      i += 1;
      continue;
    }

    // separators / punctuation pass through unchanged
    out += ch;
    i += 1;
  }

  return out;
}

// --- value parsing -----------------------------------------------------------

/**
 * Parse a Date or ISO-ish string the way `moment(value)` does (no explicit format).
 * null/undefined/"" yield an Invalid Date (never throws). NB: unlike moment, a
 * literal `undefined` does NOT resolve to "now" — a missing value stays invalid.
 */
export function parseDateValue(value: string | Date | null | undefined): Date {
  if (value instanceof Date) return value;
  if (value == null || value === '') return new Date(NaN);
  return parseISO(value);
}

/**
 * Parse a string against a moment-style format, like `moment(value, format)`.
 * date-fns is strict where moment was forgiving, so when the strict parse
 * fails this retries with moment-like tolerance: mismatched date separators
 * ("10-06-2026" against "DD.MM.YYYY" — e.g. a backend displayFormat applied
 * to a range stored as "dd-MM-yyyy") and trailing unparsed text
 * ("10-06-2026 14:30" against "DD-MM-YYYY") still parse instead of becoming
 * "Invalid date".
 */
export function parseByFormat(value: string | Date | null | undefined, momentFormat: string): Date {
  if (value instanceof Date) return value;
  // '' must stay invalid (like moment): the format-truncation fallback below
  // would otherwise degrade to an empty pattern, which date-fns parses as "now".
  if (value == null || value === '') return new Date(NaN);

  let date = dfParse(value, momentToDateFnsFormat(momentFormat), new Date());
  if (isValidDate(date)) return date;

  // Lenient retry: normalise [./-] separators on both sides to "-" ...
  const lenientValue = value.replace(/[./]/g, '-');
  const lenientFormat = momentToDateFnsFormat(momentFormat.replace(/[./]/g, '-'));
  date = dfParse(lenientValue, lenientFormat, new Date());
  if (isValidDate(date)) return date;

  // ... then drop any unparsed tail beyond the format's length (only exact
  // for fixed-width formats; anything else stays an Invalid Date, as before).
  if (lenientValue.length > momentFormat.length) {
    date = dfParse(lenientValue.slice(0, momentFormat.length), lenientFormat, new Date());
    if (isValidDate(date)) return date;
  }

  // ... or, when the format is longer than the value ("10-06-2026" against
  // "DD.MM.YYYY HH:mm"), drop the format's unmatched tail — moment filled the
  // missing time parts with zeros, which date-fns also does for absent tokens.
  if (momentFormat.length > lenientValue.length) {
    const truncated = momentToDateFnsFormat(momentFormat.replace(/[./]/g, '-').slice(0, lenientValue.length));
    date = dfParse(lenientValue, truncated, new Date());
  }
  return date;
}

// --- formatting --------------------------------------------------------------

/**
 * Format a value with a moment-style format string, mirroring
 * `moment(value).format(fmt)`. When no format is given, falls back to
 * ISO-8601 with offset (moment's default `.format()` output).
 */
export function formatDateValue(value: string | Date | null | undefined, momentFormat?: string): string {
  const date = parseDateValue(value);
  if (!isValidDate(date)) return INVALID_DATE;
  return momentFormat ? dfFormat(date, momentToDateFnsFormat(momentFormat)) : dfFormat(date, ISO_DEFAULT_PATTERN);
}

/**
 * Reproduce `moment(value).format("YYYY-MM-DDTHH:mm:ss.sss")` exactly, including
 * moment's quirk where the trailing ".sss" renders the seconds TWICE
 * (e.g. ".033" for 3s, ".4747" for 47s) instead of milliseconds — date-fns would
 * treat "sss" as a single 3-digit token and produce a different value. This
 * string is sent to the backend as the booking's local date-time, so the legacy
 * output is preserved byte-for-byte.
 */
export function formatLegacyLocalDateTime(value: string | Date = new Date()): string {
  const date = parseDateValue(value);
  if (!isValidDate(date)) return INVALID_DATE;
  return `${dfFormat(date, "yyyy-MM-dd'T'HH:mm:ss")}.${dfFormat(date, 'ss')}${dfFormat(date, 's')}`;
}

/** Mirror of `moment().format(fmt)` — formats the current instant. */
export function formatNow(momentFormat: string): string {
  return dfFormat(new Date(), momentToDateFnsFormat(momentFormat));
}

/** Mirror of `moment.utc(value).local().format(fmt)`. */
export function formatUtcToLocal(value: string | Date, momentFormat: string): string {
  const date = parseUtcDate(value);
  if (!isValidDate(date)) return INVALID_DATE;
  return dfFormat(date, momentToDateFnsFormat(momentFormat));
}

/**
 * Mirror of `moment(value, fromFormat).format(toFormat)` used to re-format
 * strings. Both formats are optional to match moment's lenient behaviour: a
 * missing fromFormat parses as ISO, a missing toFormat outputs ISO-8601.
 */
export function reformatDateValue(value: string | Date, fromFormat?: string, toFormat?: string): string {
  const date = fromFormat ? parseByFormat(value, fromFormat) : parseDateValue(value);
  if (!isValidDate(date)) return INVALID_DATE;
  return toFormat ? dfFormat(date, momentToDateFnsFormat(toFormat)) : dfFormat(date, ISO_DEFAULT_PATTERN);
}
